/**
 * Mieterportal 2.0 – Start als Verkaufs-Cockpit: Hero (Guthaben, heute, 14-Tage-Verlauf), „Verkauft!“ mit Konfetti,
 * Onboarding-Checkliste, Aktions-Kacheln, Für dich, Lagerhüter-Fahrplan, Zahlen, letzte Verkäufe, Regal-Check, App installieren.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, Notice, Seitenzustand, StatusBadge, type IconName } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { Card, Dl } from "../ui/Layout";
import { formatEuro } from "@shared/geld";
import { formatDatumZeit, formatTag, formatUhrzeit, tagVon } from "@shared/zeit";
import { type Kachel } from "@shared/schemas/mieterportal";
import { useHinweisGelesen, useMieterStart, type MieterStart as StartDaten } from "./api";
import { useNeuigkeiten, useRegalFotos } from "./portal-c-api";
import { useCockpit, usePortalSpeichern, type Cockpit } from "./cockpit-api";
import { fotoUrl, usePreisSenken } from "./artikel-api";
import { useVerkaufsMeldung } from "./live";
import { Konfetti } from "./Konfetti";
import { useT, type Schluessel, useTx } from "../i18n";

const HINWEIS_TITEL: Record<string, string> = { aufraeumen: "Bitte räum dein Regal auf", rueckgabe_kiste: "Etwas liegt in der Rückgabe-Kiste", mietende: "Deine Mietzeit endet bald", info: "Hinweis vom Laden" };
const WT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
}

export const KACHEL: Record<Kachel, { to: string; icon: IconName; text: Schluessel }> = {
  erfassen: { to: "/mieter/erfassen", icon: "camera", text: "kachel.erfassen" },
  etiketten: { to: "/mieter/etiketten", icon: "printer", text: "kachel.etiketten" },
  packliste: { to: "/mieter/packliste", icon: "package", text: "kachel.packliste" },
  regal: { to: "/mieter/regal", icon: "library", text: "kachel.regal" },
  lagerhueter: { to: "/mieter/artikel?filter=lagerhueter", icon: "clock", text: "kachel.lagerhueter" },
  abrechnung: { to: "/mieter/abrechnungen", icon: "receipt", text: "kachel.abrechnung" },
  teilen: { to: "/mieter/teilen", icon: "sparkles", text: "kachel.teilen" },
  statistik: { to: "#zahlen", icon: "list", text: "kachel.statistik" },
  aktionen: { to: "/mieter/aktionen", icon: "percent", text: "kachel.aktionen" },
  inventur: { to: "/mieter/inventur", icon: "scan-line", text: "kachel.inventur" },
  termin: { to: "/mieter/termin", icon: "calendar-days", text: "kachel.termin" },
  sammel: { to: "/mieter/sammel", icon: "inbox", text: "kachel.sammel" },
};
const WOCHENTAGE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

export function MieterStart() {
  const q = useMieterStart();
  const ck = useCockpit();
  return (
    <Seitenzustand query={q} lines={6}>
      {(d) => <Cockpit d={d} ck={ck.data} />}
    </Seitenzustand>
  );
}

function Cockpit({ d, ck }: { d: StartDaten; ck?: Cockpit }) {
  const tx = useTx();
  const t = useT();
  const einfach = !!ck?.einstellungen.anzeige?.einfach;
  const gelesen = useHinweisGelesen();
  const portal = usePortalSpeichern();
  const senken = usePreisSenken();
  const [verkauft, setVerkauft] = useState<{ betragCent: number; zeitpunkt: string; anzahl: number } | null>(null);
  const [konfetti, setKonfetti] = useState(false);
  const [install, setInstall] = useState<InstallPrompt | null>(null);
  const [installiert, setInstalliert] = useState(() => window.matchMedia("(display-mode: standalone)").matches);
  const einst = ck?.einstellungen;
  const neuigkeiten = useNeuigkeiten();
  const regalFotos = useRegalFotos();

  useVerkaufsMeldung((v) => {
    setVerkauft({ betragCent: v.betragCent, zeitpunkt: v.zeitpunkt, anzahl: v.artikelIds.length });
    if (einst?.konfetti !== false) {
      setKonfetti(true);
      setTimeout(() => setKonfetti(false), 2600);
    }
  });
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstall(e as InstallPrompt);
    };
    const onInstalled = () => setInstalliert(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const vorname = d.mieter.name.split(" ")[0] ?? "";
  const heute = tagVon();
  const stunde = new Date().getHours();
  const gruss = stunde < 11 ? "Guten Morgen" : stunde < 18 ? "Hallo" : "Guten Abend";
  const verlaufMax = Math.max(1, ...(ck?.verlauf.map((v) => v.summeCent) ?? [0]));
  const summe14 = ck?.verlauf.reduce((a, v) => a + v.summeCent, 0) ?? 0;
  const hatRegal = d.buchungen.length > 0;

  const onboarding = useMemo(() => {
    if (!ck) return null;
    const o = ck.onboarding;
    const schritte: { key: string; text: string; done: boolean; to: string }[] = [
      { key: "vertrag", text: o.vertragOffen ? "Mietvertrag bestätigen" : "Mietvertrag bestätigt", done: o.vertrag, to: d.offeneVertraege[0] ? `/mieter/vertrag/${d.offeneVertraege[0].id}` : "/mieter/regal" },
      { key: "auszahlung", text: "Auszahlung einrichten (IBAN oder bar)", done: o.auszahlung, to: "/mieter/profil" },
      { key: "artikel", text: "Ersten Artikel erfassen", done: o.ersterArtikel, to: "/mieter/erfassen" },
      { key: "etiketten", text: "Etiketten drucken", done: o.etiketten, to: "/mieter/etiketten" },
      { key: "einraeumen", text: "Im Laden einräumen und abhaken", done: o.eingeraeumt, to: "/mieter/packliste" },
    ];
    const fertig = schritte.filter((s) => s.done).length;
    return { schritte, fertig, alle: fertig === schritte.length };
  }, [ck, d.offeneVertraege]);

  const kacheln: Kachel[] = einst?.kacheln?.length ? einst.kacheln : ["erfassen", "etiketten", "packliste", "regal", "teilen", "statistik"];
  const zahl = (k: Kachel) => (k === "etiketten" ? ck?.druckliste : k === "packliste" ? ck?.packliste : k === "lagerhueter" ? ck?.lagerhueter.length : k === "aktionen" ? ck?.aktionen?.length : undefined);


  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <Konfetti aktiv={konfetti} ton={!!einst?.ton} />

      {/* Hero */}
      <section className="mp-hero" aria-label={t("start.guthaben")}>
        <span className="mp-hero__hallo">{gruss}, {vorname}</span>
        <span className="mp-hero__label">{t("start.guthaben")}</span>
        <span className="mp-hero__betrag" aria-live="polite">{formatEuro(d.guthabenCent)}</span>
        <span className={`mp-hero__heute${d.verkaeufeHeute.anzahl ? " is-joy" : ""}`}>
          <Icon name={d.verkaeufeHeute.anzahl ? "sparkles" : "clock"} size={16} />
          {d.verkaeufeHeute.anzahl ? `${t("start.heute")} ${d.verkaeufeHeute.anzahl} ${t("start.verkauft").toLowerCase()} · ${formatEuro(d.verkaeufeHeute.summeCent)}` : t("start.heuteNichts")}
        </span>
        {ck ? (
          <>
            <div className="mp-hero__bars" role="img" aria-label={`Verkäufe der letzten 14 Tage: ${formatEuro(summe14)}`}>
              {ck.verlauf.map((v) => (
                <span key={v.tag} className={[v.summeCent > 0 && "is-on", v.tag === heute && "is-heute"].filter(Boolean).join(" ")} style={{ height: `${Math.max(7, (v.summeCent / verlaufMax) * 100)}%` }} title={`${formatTag(v.tag)}: ${formatEuro(v.summeCent)}`} />
              ))}
            </div>
            <div className="mp-hero__legende"><span>{WT[new Date(`${ck.verlauf[0]!.tag}T12:00:00`).getDay()]}, vor 2 Wochen</span><span>14 Tage: {formatEuro(summe14)}</span><span>heute</span></div>
          </>
        ) : null}
      </section>

      {verkauft ? (
        <Notice tone="joy" icon="sparkles" title={tx("Verkauft!")} action={<Button variant="quiet" icon="x" onClick={() => setVerkauft(null)} aria-label="Schließen" />}>
          {verkauft.anzahl === 1 ? "Ein Artikel" : `${verkauft.anzahl} Artikel`} · {formatEuro(verkauft.betragCent)} · {formatUhrzeit(verkauft.zeitpunkt)} Uhr
        </Notice>
      ) : null}
      {d.offeneVertraege.map((v) => (
        <Notice key={v.id} tone="attention" title={`Mietvertrag für Regal ${v.regalNummer} wartet auf dich`} action={<Link to={`/mieter/vertrag/${v.id}`} className="rg-btn rg-btn--primary">Ansehen</Link>}>
          Ab {formatTag(v.start)}{v.ende ? ` bis ${formatTag(v.ende)}` : " als Miet-Abo"}. Bis zur Zustimmung ist das Regal für dich reserviert.
        </Notice>
      ))}

      {regalFotos.data?.fotos.filter((f) => f.art === "lob").slice(0, 1).map((f) => (
        <Notice key={f.id} tone="joy" icon="sparkles" title={tx("Lob vom Laden")}>{f.text} · {formatDatumZeit(f.erstelltAt)}</Notice>
      ))}
      {neuigkeiten.data?.neuigkeiten.slice(0, 2).map((n) => (
        <Notice key={n.id} tone="info" icon="bell" title={n.titel}>{n.text} <span className="sc-muted sc-small">· {d.laden.name}, {formatTag(n.erstelltAt.slice(0, 10))}</span></Notice>
      ))}

      {/* Onboarding */}
      {onboarding && !onboarding.alle && !einst?.onboardingAusgeblendet ? (
        <Card title={`Los geht’s · ${onboarding.fertig} von ${onboarding.schritte.length}`} actions={<Button variant="quiet" icon="x" aria-label="Checkliste ausblenden" onClick={() => einst && portal.mutate({ ...einst, onboardingAusgeblendet: true })} />}>
          <div className="sc-progress" aria-hidden="true"><span style={{ width: `${(onboarding.fertig / onboarding.schritte.length) * 100}%` }} /></div>
          <div className="mp-schritte" style={{ marginTop: "var(--space-3)" }}>
            {onboarding.schritte.map((s, i) => {
              const next = !s.done && onboarding.schritte.slice(0, i).every((x) => x.done);
              return (
                <Link key={s.key} to={s.to} className={["mp-schritt", s.done && "is-done", next && "is-next"].filter(Boolean).join(" ")}>
                  <span className="mp-schritt__nr">{s.done ? <Icon name="check" size={14} /> : i + 1}</span>
                  <span style={{ flex: 1 }}>{s.text}</span>
                  {!s.done ? <Icon name="chevron-right" size={18} /> : null}
                </Link>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/* Kacheln */}
      {hatRegal ? (
        <div className="mp-kacheln">
          {kacheln.map((k, i) => {
            const def = KACHEL[k];
            const n = zahl(k);
            const inhalt = (
              <>
                <Icon name={def.icon} size={26} />
                <span>{t(def.text)}</span>
                {k === "erfassen" ? <span className="mp-kachel__sub">{t("kachel.erfassenSub")}</span> : null}
                {k === "etiketten" && n ? <span className="mp-kachel__sub">{n} warten</span> : null}
                {k === "packliste" && n ? <span className="mp-kachel__sub">{n} mitbringen</span> : null}
                {n ? <span className="mp-kachel__zahl">{n}</span> : null}
              </>
            );
            if (k === "statistik") return <a key={k} href="#zahlen" className="mp-kachel">{inhalt}</a>;
            return <Link key={k} to={def.to} className={`mp-kachel${i === 0 ? " mp-kachel--primaer" : ""}`}>{inhalt}</Link>;
          })}
        </div>
      ) : null}

      {/* Für dich */}
      {d.hinweise.some((h) => !h.gelesenAt) || d.rueckgabeKiste > 0 || (hatRegal && d.artikelImRegal < 3) ? (
        <>
          <h2 className="heading">{tx("Für dich")}</h2>
          <div className="sc-col" style={{ gap: 2 }}>
            {d.hinweise.filter((h) => !h.gelesenAt).map((h) => (
              <ItemRow key={h.id} photo={false} title={HINWEIS_TITEL[h.art] ?? "Hinweis"} meta={`${h.text} · ${formatDatumZeit(h.erstelltAt)}`} action={<Button variant="quiet" icon="check" onClick={() => gelesen.mutate(h.id)}>Gelesen</Button>} tone="attention" />
            ))}
            {d.rueckgabeKiste ? <ItemRow photo={false} title={`Rückgabe-Kiste: ${d.rueckgabeKiste} Artikel`} meta="Wartet an der Kasse auf dich" action={<Link to="/mieter/rueckgabe-kiste" className="rg-btn rg-btn--quiet">Ansehen</Link>} /> : null}
            {hatRegal && d.artikelImRegal < 3 ? <ItemRow photo={false} title={d.artikelImRegal === 0 ? "Dein Regal ist noch leer" : "Dein Regal ist fast leer"} meta="Neue Artikel erfassen und mitbringen" action={<Link to="/mieter/erfassen" className="rg-btn rg-btn--primary">Erfassen</Link>} /> : null}
          </div>
        </>
      ) : null}

      {/* Ziel (MP-D) */}
      {!einfach && ck?.ziel ? (
        <Card title={`Dein Ziel ${ck.ziel.zeitraumText}`} actions={<Link to="/mieter/profil#ziel" className="rg-btn rg-btn--quiet">Ändern</Link>}>
          <div className="sc-spread">
            <span className="sc-strong">{formatEuro(ck.ziel.standCent)} von {formatEuro(ck.ziel.zielCent)}</span>
            <StatusBadge status={ck.ziel.erreicht ? "paid" : "neutral"}>{ck.ziel.erreicht ? "Geschafft" : `${ck.ziel.prozent} %`}</StatusBadge>
          </div>
          <div className="sc-progress" role="progressbar" aria-valuenow={ck.ziel.prozent} aria-valuemin={0} aria-valuemax={100} aria-label="Ziel" style={{ marginTop: "var(--space-2)" }}><span style={{ width: `${ck.ziel.prozent}%` }} /></div>
          <p className="sc-muted sc-small" style={{ margin: "var(--space-2) 0 0" }}>{ck.ziel.erreicht ? "Ziel erreicht – alles darüber ist Bonus." : `Noch ${formatEuro(ck.ziel.zielCent - ck.ziel.standCent)} bis zum Ziel. Du bekommst eine Nachricht, sobald es erreicht ist.`}</p>
        </Card>
      ) : null}

      {/* Laufende Aktionen (MP-D) */}
      {ck?.aktionen?.length ? (
        <Link to="/mieter/aktionen" className="mp-aktion-band">
          <Icon name="percent" size={20} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="sc-strong">{ck.aktionen.length === 1 ? ck.aktionen[0]!.titel : `${ck.aktionen.length} Aktionen laufen`}</span>
            <span className="sc-small" style={{ display: "block", opacity: 0.85 }}>{ck.aktionen.map((a) => a.text).join(" · ")}</span>
          </span>
          <Icon name="chevron-right" size={18} />
        </Link>
      ) : null}

      {/* Lagerhüter */}
      {!einfach && ck?.lagerhueter.length ? (
        <Card title={tx("Liegt schon länger")} actions={<Link to="/mieter/artikel?filter=lagerhueter" className="rg-btn rg-btn--quiet">Alle</Link>}>
          {ck.lagerhueter.slice(0, 3).map((a) => (
            <div key={a.id} className="mp-lager">
              {a.fotoKey ? <img className="rg-thumb" src={fotoUrl(a.fotoKey)} alt="" /> : <span className="rg-thumb rg-thumb--empty"><Icon name="image-off" size={22} /></span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="rg-row__title">{a.bezeichnung}</div>
                <div className="rg-row__meta">{formatEuro(a.preisCent)} · {a.tage} Tage · {a.text}</div>
              </div>
              {a.stufe === 3 ? (
                <Link to={`/mieter/artikel/${a.id}`} className="rg-btn rg-btn--secondary">Entscheiden</Link>
              ) : (
                <Button variant="secondary" icon="percent" loading={senken.isPending && senken.variables?.artikelIds[0] === a.id} onClick={() => senken.mutate({ artikelIds: [a.id], art: "prozent", wert: a.prozent })}>
                  −{a.prozent} %
                </Button>
              )}
            </div>
          ))}
        </Card>
      ) : null}

      {/* Zahlen */}
      {!einfach && ck && hatRegal ? (
        <Card title={t("start.zahlen")} id="zahlen">
          <div className="sc-grid">
            <div className="sc-kpi"><span className="sc-kpi__label">{t("start.umsatz")}</span><span className="sc-kpi__value">{formatEuro(ck.statistik.umsatz30Cent)}</span></div>
            <div className="sc-kpi"><span className="sc-kpi__label">{t("start.verkauft")}</span><span className="sc-kpi__value">{ck.statistik.verkauft30}</span></div>
            <div className="sc-kpi"><span className="sc-kpi__label">{t("start.preis")}</span><span className="sc-kpi__value">{ck.statistik.verkauft30 ? formatEuro(ck.statistik.durchschnittPreisCent) : "–"}</span></div>
            <div className="sc-kpi"><span className="sc-kpi__label">{t("start.imRegal")}</span><span className="sc-kpi__value">{ck.statistik.verkauft30 ? `${ck.statistik.durchschnittTage} Tage` : "–"}</span></div>
          </div>
          {ck.statistik.bestseller.length ? (
            <>
              <h3 className="heading" style={{ marginTop: "var(--space-4)" }}>Läuft am besten</h3>
              <Dl rows={ck.statistik.bestseller.map((b) => [b.kategorie, `${b.anzahl} verkauft`] as [string, string])} />
            </>
          ) : (
            <p className="sc-muted sc-small" style={{ marginTop: "var(--space-3)" }}>Mit Kategorie beim Erfassen siehst du hier, was am besten läuft.</p>
          )}
        </Card>
      ) : null}

      {/* Saison-Tipp (MP-D) */}
      {!einfach && ck?.saison && hatRegal ? (
        <Card title={`Saison-Tipp ${ck.saison.monat}`}>
          <p className="sc-strong" style={{ margin: 0 }}>{ck.saison.titel}</p>
          <p className="sc-muted" style={{ margin: "var(--space-1) 0 0" }}>{ck.saison.text}</p>
          {ck.saison.vorjahr.length ? <p className="sc-small" style={{ margin: "var(--space-2) 0 0" }}>Im {ck.saison.monat} des Vorjahres lief hier: {ck.saison.vorjahr.map((v) => v.kategorie).join(", ")}.</p> : null}
          <p className="sc-small" style={{ margin: "var(--space-2) 0 0" }}>{ck.saison.passendImRegal ? `${ck.saison.passendImRegal} ${ck.saison.passendImRegal === 1 ? "Artikel in deinem Regal passt" : "Artikel in deinem Regal passen"} zur Saison.` : "Noch nichts Passendes im Regal – vielleicht ist zu Hause etwas?"}</p>
        </Card>
      ) : null}

      {/* Was läuft im Laden (MP-D) */}
      {!einfach && ck?.laden && hatRegal && ck.laden.verkauft >= 5 ? (
        <Card title="Was läuft im Laden · 30 Tage">
          <p className="sc-muted sc-small" style={{ margin: "0 0 var(--space-2)" }}>Alle Regale zusammen, ohne Namen.</p>
          {ck.laden.kategorien.length ? <Dl rows={ck.laden.kategorien.map((k) => [k.kategorie, `${k.anteil} %`] as [string, string])} /> : null}
          <h3 className="heading" style={{ marginTop: "var(--space-3)" }}>Preisklassen</h3>
          <div className="sc-col" style={{ gap: 4 }}>
            {ck.laden.preisklassen.map((p) => (
              <div key={p.text} className="sc-row" style={{ gap: "var(--space-2)" }}>
                <span className="sc-small" style={{ width: 90, flex: "none" }}>{p.text}</span>
                <div className="sc-progress" aria-hidden="true"><span style={{ width: `${p.anteil}%` }} /></div>
                <span className="sc-small sc-muted" style={{ width: 36, textAlign: "right" }}>{p.anteil} %</span>
              </div>
            ))}
          </div>
          {ck.laden.beispiele?.length ? (
            <>
              <h3 className="heading" style={{ marginTop: "var(--space-3)" }}>Zuletzt verkauft im Laden</h3>
              <div className="mp-beispiele" aria-label="Beispiele aus anderen Regalen">
                {ck.laden.beispiele.map((b) => (
                  <figure key={b.id} className="mp-beispiel">
                    <img src={b.fotoUrl} alt="" loading="lazy" />
                    <figcaption><span>{formatEuro(b.preisCent)}</span><span className="sc-muted sc-small">{b.kategorie || b.bezeichnung}</span></figcaption>
                  </figure>
                ))}
              </div>
            </>
          ) : null}
          {ck.laden.stunden.length ? (
            <p className="sc-small" style={{ margin: "var(--space-3) 0 0" }}>
              Meiste Verkäufe: {[...ck.laden.wochentage].sort((a, b) => b.anteil - a.anteil).slice(0, 2).map((w) => WOCHENTAGE[w.wt]).join(" und ")}, meist {ck.laden.stunden.map((h) => `${h.stunde}–${h.stunde + 1} Uhr`).join(", ")}.
            </p>
          ) : null}
        </Card>
      ) : null}

      {/* Mein Regal im Vergleich (MP-D) */}
      {!einfach && ck?.vergleich && hatRegal ? (
        <Card title="Mein Regal im Vergleich · 30 Tage">
          <p className="sc-strong" style={{ margin: 0 }}>{ck.vergleich.text}</p>
          <div className="sc-col" style={{ gap: 6, marginTop: "var(--space-2)" }}>
            {[
              { text: "Du", cent: ck.vergleich.meine.umsatzCent, n: ck.vergleich.meine.verkauft },
              { text: "Mitte im Laden", cent: ck.vergleich.median.umsatzCent, n: ck.vergleich.median.verkauft },
            ].map((z) => (
              <div key={z.text} className="sc-row" style={{ gap: "var(--space-2)" }}>
                <span className="sc-small" style={{ width: 110, flex: "none" }}>{z.text}</span>
                <div className="sc-progress" aria-hidden="true"><span style={{ width: `${Math.round((z.cent / Math.max(1, ck.vergleich!.meine.umsatzCent, ck.vergleich!.median.umsatzCent)) * 100)}%` }} /></div>
                <span className="sc-small sc-muted" style={{ width: 96, textAlign: "right" }}>{formatEuro(z.cent)} · {z.n}</span>
              </div>
            ))}
          </div>
          <p className="sc-muted sc-small" style={{ margin: "var(--space-2) 0 0" }}>Verglichen mit {ck.vergleich.mieter} Mietern im Laden, ohne Namen.</p>
        </Card>
      ) : null}

      {/* Letzte Verkäufe */}
      {!hatRegal && d.offeneVertraege.length === 0 ? (
        <EmptyState illustration="empty-shelf" title={tx("Noch kein Regal")}>Sobald der Laden ein Regal für dich bucht, siehst du hier Vertrag, Verkäufe und Guthaben.</EmptyState>
      ) : (
        <>
          <div className="sc-spread">
            <h2 className="heading">{t("start.letzteVerkaeufe")}</h2>
            <StatusBadge status="paid"><Icon name="circle" size={10} /> {t("start.live")}</StatusBadge>
          </div>
          {d.letzteVerkaeufe.length === 0 ? (
            <p className="sc-muted">{t("start.nochNichts")}</p>
          ) : (
            <div>
              {d.letzteVerkaeufe.slice(0, 5).map((a) => (
                <ItemRow key={a.id} title={a.bezeichnung} photo={fotoUrl(a.fotoKey)} meta={`${a.statusAt.slice(0, 10) === heute ? "Heute" : formatTag(a.statusAt.slice(0, 10))}, ${formatUhrzeit(a.statusAt)}`} price={a.preisCent} tone={a.statusAt.slice(0, 10) === heute ? "joy" : undefined} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Regal-Check */}
      {!einfach && ck && hatRegal ? (
        <Card title={t("start.regalCheck")}>
          <div className="sc-row" style={{ gap: "var(--space-4)", alignItems: "flex-start" }}>
            <div className="mp-ring" style={{ ["--p" as string]: ck.regalCheck.punkte }} role="img" aria-label={`${ck.regalCheck.punkte} von 100 Punkten`}><span>{ck.regalCheck.punkte}</span></div>
            <div className="sc-col" style={{ gap: 6, flex: 1 }}>
              {ck.regalCheck.punkte >= 90 ? <p className="sc-strong">Top – dein Regal ist in Bestform.</p> : null}
              {ck.regalCheck.tipps.map((t) => (
                <Link key={t.text} to={t.ziel} className="sc-row" style={{ color: "inherit", textDecoration: "none" }}>
                  <Icon name="chevron-right" size={16} /> <span>{t.text}</span>
                </Link>
              ))}
            </div>
          </div>
        </Card>
      ) : null}

      {!installiert && !einfach ? (
        <Card title={t("start.app")}>
          <p className="sc-muted">Zum Home-Bildschirm hinzufügen – dann öffnet sich das Portal wie eine App.{iOS ? " Auf dem iPhone: Teilen-Symbol → „Zum Home-Bildschirm“." : ""}</p>
          {install ? <Button variant="primary" icon="plus" onClick={() => void install.prompt()}>Zum Home-Bildschirm hinzufügen</Button> : null}
        </Card>
      ) : null}
    </div>
  );
}
